
import { supabase } from '@/integrations/supabase/client';
import { isValidEmail, sanitizeEmailInput, isValidMetadata, validatePasswordStrength } from '../utils/validation';
import { AuthError, handleAuthError } from '../utils/errors';

export async function loginWithEmailPassword(
  email: string, 
  password: string
) {
  try {
    // Input validation
    if (!email || !password) {
      throw new AuthError('Email and password are required', 'auth/invalid-input');
    }
    
    if (!isValidEmail(email)) {
      throw new AuthError('Invalid email format', 'auth/invalid-email');
    }
    
    console.log(`Attempting to sign in with email: ${email}`);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password
    });
    
    if (error) {
      console.error('Supabase login error:', error);
      throw error;
    }
    
    console.log('Login successful, session established', data.session?.expires_at);
    return data;
  } catch (error) {
    return handleAuthError(error);
  }
}

export async function loginWithProvider(provider: 'google' | 'apple') {
  try {
    console.log(`Attempting to sign in with ${provider}...`);
    
    // Generate cryptographically secure state parameter
    const state = btoa(JSON.stringify({
      provider,
      timestamp: Date.now(),
      nonce: crypto.getRandomValues(new Uint32Array(4)).join('')
    }));
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        scopes: provider === 'google' ? 'profile email' : undefined,
        queryParams: {
          state
        }
      }
    });
    
    if (error) {
      console.error(`Error initiating ${provider} login:`, error);
      throw error;
    }
    
    console.log(`${provider} OAuth flow initiated successfully:`, data);
    
    return data;
  } catch (error) {
    console.error(`Failed to sign in with ${provider}:`, error);
    return handleAuthError(error);
  }
}

export async function signUpWithEmailPassword(email: string, password: string, metadata?: Record<string, any>) {
  try {
    // Input validation
    if (!email || !password) {
      throw new AuthError('Email and password are required', 'auth/invalid-input');
    }
    
    if (!isValidEmail(email)) {
      throw new AuthError('Invalid email format', 'auth/invalid-email');
    }
    
    // Validate metadata if provided
    if (metadata && !isValidMetadata(metadata)) {
      throw new AuthError('Invalid metadata provided', 'auth/invalid-metadata');
    }
    
    // Validate password strength
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      throw new AuthError(`Password requirements not met: ${passwordValidation.errors.join(', ')}`, 'auth/weak-password');
    }

    const { data, error } = await supabase.auth.signUp({
      email: sanitizeEmailInput(email),
      password,
      options: {
        data: metadata,
        // CRITICAL: Always include email redirect for security
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    return handleAuthError(error);
  }
}

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    return handleAuthError(error);
  }
}
